import React, { useState, useEffect } from 'react';
import { PlusIcon, TrashIcon, ExclamationTriangleIcon } from '@heroicons/react/24/outline';

interface CommissionTier {
  id?: number;
  min_margin: number;
  max_margin: number;
  commission_percent: number;
}

interface CommissionTierEditorProps {
  tiers: CommissionTier[];
  onChange: (tiers: CommissionTier[]) => void;
  reportType: 'exteriors' | 'cody';
}

const CommissionTierEditor: React.FC<CommissionTierEditorProps> = ({
  tiers,
  onChange,
  reportType
}) => {
  // Default tiers if none provided
  const defaultTiers: CommissionTier[] = [
    { min_margin: 0, max_margin: 40, commission_percent: 3 },
    { min_margin: 40, max_margin: 50, commission_percent: 4 },
    { min_margin: 50, max_margin: 60, commission_percent: 5 },
    { min_margin: 60, max_margin: 100, commission_percent: 6 }
  ];
  
  const [localTiers, setLocalTiers] = useState<CommissionTier[]>(tiers || defaultTiers);
  const [errors, setErrors] = useState<string[]>([]);

  useEffect(() => {
    setLocalTiers(tiers || defaultTiers);
  }, [tiers]);

  const validateTiers = (tiersToValidate: CommissionTier[]): string[] => {
    const validationErrors: string[] = [];
    
    // Sort tiers by min_margin
    const sortedTiers = [...tiersToValidate].sort((a, b) => a.min_margin - b.min_margin);
    
    for (let i = 0; i < sortedTiers.length; i++) {
      const tier = sortedTiers[i];
      
      // Check if min is less than max
      if (tier.min_margin >= tier.max_margin) {
        validationErrors.push(`Tier ${i + 1}: Minimum margin must be less than maximum margin`);
      }
      
      // Check for gaps or overlaps
      if (i > 0) {
        const prevTier = sortedTiers[i - 1];
        if (Math.abs(tier.min_margin - prevTier.max_margin) > 0.01) {
          validationErrors.push(`Gap or overlap detected between tiers ${i} and ${i + 1}`);
        }
      }
      
      // Check valid percentages
      if (tier.min_margin < 0 || tier.max_margin > 100) {
        validationErrors.push(`Tier ${i + 1}: Margins must be between 0% and 100%`);
      }
      
      if (tier.commission_percent < 0 || tier.commission_percent > 100) {
        validationErrors.push(`Tier ${i + 1}: Commission must be between 0% and 100%`);
      }
    }
    
    return validationErrors;
  };

  const handleTierChange = (index: number, field: keyof CommissionTier, value: number) => {
    const newTiers = [...localTiers];
    newTiers[index] = {
      ...newTiers[index],
      [field]: value
    };
    
    // Auto-adjust boundaries to maintain continuity
    if (field === 'max_margin' && index < newTiers.length - 1) {
      newTiers[index + 1].min_margin = value;
    } else if (field === 'min_margin' && index > 0) {
      newTiers[index - 1].max_margin = value;
    }
    
    setLocalTiers(newTiers);
    const validationErrors = validateTiers(newTiers);
    setErrors(validationErrors);
    
    if (validationErrors.length === 0) {
      onChange(newTiers);
    }
  };

  const addTier = () => {
    if (localTiers.length === 0) {
      const newTier: CommissionTier = {
        min_margin: 0,
        max_margin: 100,
        commission_percent: 5
      };
      const newTiers = [newTier];
      setLocalTiers(newTiers);
      onChange(newTiers);
      return;
    }
    
    const lastTier = localTiers[localTiers.length - 1];
    const newTier: CommissionTier = {
      min_margin: lastTier.max_margin,
      max_margin: 100,
      commission_percent: lastTier.commission_percent + 1
    };
    
    const newTiers = [...localTiers, newTier];
    setLocalTiers(newTiers);
    onChange(newTiers);
  };

  const removeTier = (index: number) => {
    if (localTiers.length <= 1) {
      setErrors(['Cannot remove the last tier']);
      return;
    }
    
    const newTiers = localTiers.filter((_, i) => i !== index);
    
    // Adjust boundaries
    if (index > 0 && index < localTiers.length - 1) {
      newTiers[index - 1].max_margin = newTiers[index].min_margin;
    }
    
    setLocalTiers(newTiers);
    const validationErrors = validateTiers(newTiers);
    setErrors(validationErrors);
    
    if (validationErrors.length === 0) {
      onChange(newTiers);
    }
  };

  const getTierWarning = (tier: CommissionTier, index: number): string | null => {
    // Check for common tier boundaries
    if (index > 0 && index < localTiers.length && (tier.min_margin === 40 || tier.min_margin === 50 || tier.min_margin === 60)) {
      const prevTier = localTiers[index - 1];
      if (prevTier) {
        const commissionJump = tier.commission_percent - prevTier.commission_percent;
        if (commissionJump > 2) {
          return `Large commission jump of ${commissionJump}% at ${tier.min_margin}% margin boundary`;
        }
      }
    }
    
    return null;
  };

  if (reportType === 'cody') {
    return (
      <div className="bg-gray-50 rounded-lg p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Commission Structure</h3>
        <div className="text-gray-600">
          <p className="mb-2">Cody reports use a fixed commission structure:</p>
          <div className="bg-white rounded p-4 font-mono text-sm">
            Sales Commission = Net Profit ÷ 2
          </div>
          <p className="mt-2 text-sm text-gray-500">
            This calculation cannot be modified through tier configuration.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white border border-gray-200 rounded-lg p-6">
      <div className="flex justify-between items-center mb-4">
        <h3 className="text-lg font-semibold text-gray-900">Commission Tier Structure</h3>
        <button
          onClick={addTier}
          className="flex items-center px-3 py-1.5 text-sm bg-blue-600 text-white rounded-lg hover:bg-blue-700"
        >
          <PlusIcon className="h-4 w-4 mr-1" />
          Add Tier
        </button>
      </div>

      {errors.length > 0 && (
        <div className="mb-4 p-4 bg-red-50 rounded-lg">
          <h4 className="text-sm font-semibold text-red-800 mb-2">Validation Errors:</h4>
          <ul className="text-sm text-red-700 space-y-1">
            {errors.map((error, idx) => (
              <li key={idx}>• {error}</li>
            ))}
          </ul>
        </div>
      )}

      <div className="space-y-3">
        {localTiers && localTiers.length > 0 ? (
          localTiers.map((tier, index) => {
            const warning = getTierWarning(tier, index);
            
            return (
            <div key={index} className="border border-gray-200 rounded-lg p-4">
              <div className="grid grid-cols-4 gap-4">
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">
                    Min Margin (%)
                  </label>
                  <input
                    type="number"
                    min="0"
                    max="100"
                    step="0.01"
                    value={tier.min_margin}
                    onChange={(e) => handleTierChange(index, 'min_margin', parseFloat(e.target.value))}
                    className="w-full px-2 py-1 text-sm border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    disabled={index === 0}
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">
                    Max Margin (%)
                  </label>
                  <input
                    type="number"
                    min="0"
                    max="100"
                    step="0.01"
                    value={tier.max_margin}
                    onChange={(e) => handleTierChange(index, 'max_margin', parseFloat(e.target.value))}
                    className="w-full px-2 py-1 text-sm border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    disabled={index === localTiers.length - 1}
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">
                    Commission (%)
                  </label>
                  <input
                    type="number"
                    min="0"
                    max="100"
                    step="0.1"
                    value={tier.commission_percent}
                    onChange={(e) => handleTierChange(index, 'commission_percent', parseFloat(e.target.value))}
                    className="w-full px-2 py-1 text-sm border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>
                <div className="flex items-end">
                  <button
                    onClick={() => removeTier(index)}
                    disabled={localTiers.length <= 1}
                    className="p-2 text-red-600 hover:bg-red-50 rounded disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <TrashIcon className="h-4 w-4" />
                  </button>
                </div>
              </div>
              
              {warning && (
                <div className="mt-2 flex items-start text-xs text-yellow-700 bg-yellow-50 p-2 rounded">
                  <ExclamationTriangleIcon className="h-4 w-4 mr-1 flex-shrink-0 mt-0.5" />
                  <span>{warning}</span>
                </div>
              )}
            </div>
          );
        })
        ) : (
          <div className="text-center text-gray-500 py-8">
            No commission tiers configured. Click &quot;Add Tier&quot; to create one.
          </div>
        )}
      </div>

      <div className="mt-4 p-4 bg-blue-50 rounded-lg">
        <h4 className="text-sm font-semibold text-blue-900 mb-2">Tier Visualization</h4>
        <div className="flex items-center space-x-1">
          {localTiers && localTiers.length > 0 ? (
            localTiers.map((tier, index) => {
              const width = tier.max_margin - tier.min_margin;
              const intensity = Math.min(tier.commission_percent * 10, 100);
              
              return (
              <div
                key={index}
                className="relative group"
                style={{ width: `${width}%` }}
              >
                <div
                  className="h-8 rounded transition-all"
                  style={{
                    backgroundColor: `rgba(59, 130, 246, ${intensity / 100})`,
                    border: '1px solid rgba(59, 130, 246, 0.5)'
                  }}
                />
                <div className="absolute inset-0 flex items-center justify-center text-xs font-medium">
                  {tier.commission_percent}%
                </div>
                <div className="absolute -bottom-6 left-0 right-0 text-center text-xs text-gray-600">
                  {tier.min_margin}-{tier.max_margin}%
                </div>
              </div>
            );
          })
          ) : (
            <div className="w-full h-8 bg-gray-200 rounded flex items-center justify-center text-xs text-gray-500">
              No tiers to visualize
            </div>
          )}
        </div>
        <div className="mt-8 text-xs text-gray-600">
          Gross Margin Range
        </div>
      </div>
    </div>
  );
};

export default CommissionTierEditor;